import React from 'react';
import { FormDrawer } from './FormDrawer';
import { capitalizeWords, formatChileanRut, normalizeProperCaseText } from '../../utils/textUtils';

const STATUS_OPTIONS = [
  { value: 'Activo', label: 'Activo' },
  { value: 'Inactivo', label: 'Inactivo' },
];

const ADDRESS_TYPE_OPTIONS = [
  { value: 'Facturación', label: 'Facturación' },
  { value: 'Sucursal', label: 'Sucursal' },
  { value: 'Bodega', label: 'Bodega' },
  { value: 'Oficina', label: 'Oficina' },
  { value: 'Otro', label: 'Otro' },
];

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'Corriente', label: 'Corriente' },
  { value: 'Vista', label: 'Vista' },
  { value: 'Ahorro', label: 'Ahorro' },
  { value: 'Otra', label: 'Otra' },
];

const DEFAULT_CURRENCY_OPTIONS = [
  { value: 'CLP', label: 'CLP' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

const applyNameCase = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return String(value || '');
  return capitalizeWords(trimmed);
};

const emptyAddress = () => ({
  address_type: 'Facturación',
  address: '',
  commune: '',
  city: '',
  region: '',
  country: 'Chile',
  notes: '',
});

const emptyBankAccount = () => ({
  bank_name: '',
  account_type: 'Corriente',
  account_number: '',
  account_holder: '',
  holder_rut: '',
  currency: 'CLP',
  status: 'Activo',
  notes: '',
});

const sectionCardStyle = (colors) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 8,
  padding: 10,
  borderRadius: 8,
  border: `1px solid ${colors.sectionBorder}`,
  background: colors.sectionBg,
});

export const SuppliersForm = ({
  isOpen,
  onClose,
  onSave,
  isSaving = false,
  isDarkMode = false,
  isEditing = false,
  formData,
  onChange,
  formError = '',
  paymentConditionOptions = [],
  supplierTypeOptions = [],
  currencyOptions = [],
  zIndex = 10000,
}) => {
  const contacts = Array.isArray(formData?.contacts) ? formData.contacts : [];
  const addresses = Array.isArray(formData?.addresses) ? formData.addresses : [];
  const bankAccounts = Array.isArray(formData?.bank_accounts) ? formData.bank_accounts : [];
  const selectedTerms = Array.isArray(formData?.payment_conditions) ? formData.payment_conditions : [];
  const bankCurrencyOptions =
    currencyOptions.length > 0 ? currencyOptions : DEFAULT_CURRENCY_OPTIONS;

  const updateContact = (index, key, value) => {
    const next = contacts.map((contact, idx) => (idx === index ? { ...contact, [key]: value } : contact));
    onChange('contacts', next);
  };

  const addContact = () => {
    onChange('contacts', [
      ...contacts,
      { id: null, full_name: '', role: '', email: '', phone: '', status: 'Activo' },
    ]);
  };

  const removeContact = (index) => {
    onChange(
      'contacts',
      contacts.filter((_, idx) => idx !== index)
    );
  };

  const updateAddress = (index, key, value) => {
    const next = addresses.map((row, idx) => (idx === index ? { ...row, [key]: value } : row));
    onChange('addresses', next);
  };

  const addAddress = () => {
    onChange('addresses', [...addresses, emptyAddress()]);
  };

  const removeAddress = (index) => {
    onChange(
      'addresses',
      addresses.filter((_, idx) => idx !== index)
    );
  };

  const updateBankAccount = (index, key, value) => {
    const next = bankAccounts.map((row, idx) => (idx === index ? { ...row, [key]: value } : row));
    onChange('bank_accounts', next);
  };

  const addBankAccount = () => {
    onChange('bank_accounts', [...bankAccounts, emptyBankAccount()]);
  };

  const removeBankAccount = (index) => {
    onChange(
      'bank_accounts',
      bankAccounts.filter((_, idx) => idx !== index)
    );
  };

  const togglePaymentTerm = (term) => {
    const exists = selectedTerms.includes(term);
    const next = exists ? selectedTerms.filter((item) => item !== term) : [...selectedTerms, term];
    onChange('payment_conditions', next);
  };

  const optionValues = new Set(paymentConditionOptions.map((opt) => opt.value));
  const orphanTerms = selectedTerms.filter((term) => !optionValues.has(term));

  const sections = [
    {
      key: 'basic',
      title: 'Datos básicos',
      fields: [
        { key: 'supplier_code', label: 'Código', disabled: true, readOnly: true },
        {
          key: 'name',
          label: 'Nombre',
          type: 'custom',
          render: ({ value, onChange: onNameChange, colors, baseInputStyle }) => (
            <div>
              <label style={{ fontSize: '12px', color: colors.text, display: 'block' }}>Nombre</label>
              <input
                style={{ ...baseInputStyle, marginTop: '6px' }}
                value={value || ''}
                onChange={(event) => onNameChange(event.target.value)}
                onBlur={() => {
                  const current = String(formData?.name || '');
                  const normalized = applyNameCase(current);
                  if (normalized !== current) onChange('name', normalized);
                }}
                placeholder="Nombre del proveedor"
              />
            </div>
          ),
        },
        {
          key: 'business_name',
          label: 'Razón social',
          type: 'custom',
          render: ({ value, onChange: onBusinessChange, colors, baseInputStyle }) => (
            <label style={{ fontSize: '12px', color: colors.text, display: 'flex', flexDirection: 'column' }}>
              Razón social
              <input
                style={{ ...baseInputStyle, marginTop: '6px' }}
                value={value || ''}
                onChange={(event) => onBusinessChange(event.target.value)}
                onBlur={() => {
                  const current = String(formData?.business_name || '');
                  const normalized = applyNameCase(current);
                  if (normalized !== current) onChange('business_name', normalized);
                }}
              />
            </label>
          ),
        },
        {
          key: 'rut',
          label: 'RUT',
          type: 'custom',
          render: ({ value, onChange: onRutChange, colors, baseInputStyle }) => (
            <label style={{ fontSize: '12px', color: colors.text, display: 'flex', flexDirection: 'column' }}>
              RUT
              <input
                style={{ ...baseInputStyle, marginTop: '6px' }}
                value={value || ''}
                placeholder="12.345.678-9"
                inputMode="text"
                autoComplete="off"
                onChange={(event) => onRutChange(formatChileanRut(event.target.value))}
                onBlur={() => {
                  const current = String(formData?.rut || '');
                  const normalized = formatChileanRut(current);
                  if (normalized !== current) onChange('rut', normalized);
                }}
              />
            </label>
          ),
        },
        {
          key: 'supplier_type',
          label: 'Tipo de proveedor',
          type: 'select',
          options: [
            { value: '', label: 'Seleccionar tipo' },
            ...supplierTypeOptions,
            ...(formData?.supplier_type &&
            !supplierTypeOptions.some((opt) => opt.value === formData.supplier_type)
              ? [{ value: formData.supplier_type, label: formData.supplier_type }]
              : []),
          ],
        },
        {
          key: 'status',
          label: 'Estado',
          type: 'select',
          options: STATUS_OPTIONS,
        },
      ],
    },
    {
      key: 'addresses',
      title: 'Direcciones',
      fields: [
        {
          key: 'addresses',
          fullWidth: true,
          type: 'custom',
          render: ({ colors, baseInputStyle }) => (
            <div style={{ display: 'grid', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: colors.muted }}>
                  Puedes registrar varias direcciones (facturación, sucursal, bodega, etc.).
                </span>
                <button
                  type="button"
                  onClick={addAddress}
                  style={{
                    height: '30px',
                    padding: '0 10px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.inputBorder}`,
                    background: 'transparent',
                    color: colors.text,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  Agregar dirección
                </button>
              </div>
              {addresses.length === 0 ? (
                <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>Sin direcciones aún.</p>
              ) : (
                addresses.map((row, index) => (
                  <div key={`address-${index}`} style={sectionCardStyle(colors)}>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Tipo
                      <select
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.address_type || 'Facturación'}
                        onChange={(event) => updateAddress(index, 'address_type', event.target.value)}
                      >
                        {ADDRESS_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      País
                      <input
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.country || ''}
                        onChange={(event) => updateAddress(index, 'country', event.target.value)}
                        onBlur={() => {
                          const current = String(row.country || '');
                          const normalized = applyNameCase(current) || 'Chile';
                          if (normalized !== current) updateAddress(index, 'country', normalized);
                        }}
                      />
                    </label>
                    <label
                      style={{
                        fontSize: 12,
                        color: colors.text,
                        display: 'flex',
                        flexDirection: 'column',
                        gridColumn: '1 / -1',
                      }}
                    >
                      Dirección
                      <input
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.address || ''}
                        onChange={(event) => updateAddress(index, 'address', event.target.value)}
                        onBlur={() => {
                          const current = String(row.address || '');
                          const normalized = normalizeProperCaseText(current);
                          if (normalized !== current) updateAddress(index, 'address', normalized);
                        }}
                      />
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Comuna
                      <input
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.commune || ''}
                        onChange={(event) => updateAddress(index, 'commune', event.target.value)}
                        onBlur={() => {
                          const current = String(row.commune || '');
                          const normalized = applyNameCase(current);
                          if (normalized !== current) updateAddress(index, 'commune', normalized);
                        }}
                      />
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Ciudad
                      <input
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.city || ''}
                        onChange={(event) => updateAddress(index, 'city', event.target.value)}
                        onBlur={() => {
                          const current = String(row.city || '');
                          const normalized = applyNameCase(current);
                          if (normalized !== current) updateAddress(index, 'city', normalized);
                        }}
                      />
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Región
                      <input
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.region || ''}
                        onChange={(event) => updateAddress(index, 'region', event.target.value)}
                        onBlur={() => {
                          const current = String(row.region || '');
                          const normalized = applyNameCase(current);
                          if (normalized !== current) updateAddress(index, 'region', normalized);
                        }}
                      />
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Notas
                      <input
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.notes || ''}
                        onChange={(event) => updateAddress(index, 'notes', event.target.value)}
                      />
                    </label>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => removeAddress(index)}
                        style={{
                          height: 34,
                          padding: '0 12px',
                          borderRadius: 8,
                          border: `1px solid ${colors.inputBorder}`,
                          background: 'transparent',
                          color: '#b91c1c',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Quitar
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          ),
        },
        {
          key: 'payment_conditions',
          label: 'Condiciones de pago',
          fullWidth: true,
          type: 'custom',
          render: ({ colors }) => (
            <div>
              <div style={{ fontSize: '12px', color: colors.text, marginBottom: 8 }}>
                Condiciones de pago
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[...paymentConditionOptions.map((opt) => opt.value), ...orphanTerms].map((term) => {
                  const checked = selectedTerms.includes(term);
                  return (
                    <label
                      key={term}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 10px',
                        borderRadius: 8,
                        border: `1px solid ${checked ? colors.text : colors.inputBorder}`,
                        background: checked ? colors.sectionBg : 'transparent',
                        fontSize: 12,
                        color: colors.text,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePaymentTerm(term)}
                      />
                      {term}
                    </label>
                  );
                })}
              </div>
              {paymentConditionOptions.length === 0 ? (
                <p style={{ margin: '8px 0 0', fontSize: 11, color: colors.muted }}>
                  No hay condiciones activas. Configúralas en Adquisiciones → Ajustes.
                </p>
              ) : null}
            </div>
          ),
        },
      ],
    },
    {
      key: 'bank_accounts',
      title: 'Cuentas bancarias',
      fields: [
        {
          key: 'bank_accounts',
          fullWidth: true,
          type: 'custom',
          render: ({ colors, baseInputStyle }) => (
            <div style={{ display: 'grid', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: colors.muted }}>
                  Puedes registrar varias cuentas y marcarlas como activas o inactivas.
                </span>
                <button
                  type="button"
                  onClick={addBankAccount}
                  style={{
                    height: '30px',
                    padding: '0 10px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.inputBorder}`,
                    background: 'transparent',
                    color: colors.text,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  Agregar cuenta
                </button>
              </div>
              {bankAccounts.length === 0 ? (
                <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>Sin cuentas bancarias aún.</p>
              ) : (
                bankAccounts.map((row, index) => (
                  <div key={`bank-${index}`} style={sectionCardStyle(colors)}>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Banco
                      <input
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.bank_name || ''}
                        onChange={(event) => updateBankAccount(index, 'bank_name', event.target.value)}
                        onBlur={() => {
                          const current = String(row.bank_name || '');
                          const normalized = applyNameCase(current);
                          if (normalized !== current) updateBankAccount(index, 'bank_name', normalized);
                        }}
                      />
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Tipo de cuenta
                      <select
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.account_type || 'Corriente'}
                        onChange={(event) => updateBankAccount(index, 'account_type', event.target.value)}
                      >
                        {ACCOUNT_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      N° de cuenta
                      <input
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.account_number || ''}
                        onChange={(event) => updateBankAccount(index, 'account_number', event.target.value)}
                      />
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Titular
                      <input
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.account_holder || ''}
                        onChange={(event) => updateBankAccount(index, 'account_holder', event.target.value)}
                        onBlur={() => {
                          const current = String(row.account_holder || '');
                          const normalized = applyNameCase(current);
                          if (normalized !== current) updateBankAccount(index, 'account_holder', normalized);
                        }}
                      />
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      RUT titular
                      <input
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.holder_rut || ''}
                        placeholder="12.345.678-9"
                        onChange={(event) =>
                          updateBankAccount(index, 'holder_rut', formatChileanRut(event.target.value))
                        }
                      />
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Moneda
                      <select
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.currency || 'CLP'}
                        onChange={(event) => updateBankAccount(index, 'currency', event.target.value)}
                      >
                        {[
                          ...bankCurrencyOptions,
                          ...(row.currency &&
                          !bankCurrencyOptions.some((opt) => opt.value === row.currency)
                            ? [{ value: row.currency, label: row.currency }]
                            : []),
                        ].map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Estado
                      <select
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.status || 'Activo'}
                        onChange={(event) => updateBankAccount(index, 'status', event.target.value)}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                      Notas
                      <input
                        style={{ ...baseInputStyle, marginTop: 6 }}
                        value={row.notes || ''}
                        onChange={(event) => updateBankAccount(index, 'notes', event.target.value)}
                      />
                    </label>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => removeBankAccount(index)}
                        style={{
                          height: 34,
                          padding: '0 12px',
                          borderRadius: 8,
                          border: `1px solid ${colors.inputBorder}`,
                          background: 'transparent',
                          color: '#b91c1c',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Quitar
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          ),
        },
      ],
    },
    {
      key: 'contacts',
      title: 'Contactos',
      fields: [
        {
          key: 'contacts',
          fullWidth: true,
          type: 'custom',
          render: ({ value, colors, baseInputStyle }) => {
            const localContacts = Array.isArray(value) ? value : [];
            return (
              <div style={{ display: 'grid', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: colors.muted }}>
                    Personas de contacto asociadas a este proveedor.
                  </span>
                  <button
                    type="button"
                    onClick={addContact}
                    style={{
                      height: '30px',
                      padding: '0 10px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.inputBorder}`,
                      background: 'transparent',
                      color: colors.text,
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    Agregar contacto
                  </button>
                </div>
                {localContacts.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>Sin contactos aún.</p>
                ) : (
                  localContacts.map((contact, index) => (
                    <div
                      key={contact.id || `new-${index}`}
                      style={sectionCardStyle(colors)}
                    >
                      <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                        Nombre
                        <input
                          style={{ ...baseInputStyle, marginTop: 6 }}
                          value={contact.full_name || ''}
                          onChange={(event) => updateContact(index, 'full_name', event.target.value)}
                          onBlur={() => {
                            const current = String(contact.full_name || '');
                            const normalized = applyNameCase(current);
                            if (normalized !== current) updateContact(index, 'full_name', normalized);
                          }}
                        />
                      </label>
                      <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                        Cargo
                        <input
                          style={{ ...baseInputStyle, marginTop: 6 }}
                          value={contact.role || ''}
                          onChange={(event) => updateContact(index, 'role', event.target.value)}
                          onBlur={() => {
                            const current = String(contact.role || '');
                            const normalized = applyNameCase(current);
                            if (normalized !== current) updateContact(index, 'role', normalized);
                          }}
                        />
                      </label>
                      <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                        Correo
                        <input
                          style={{ ...baseInputStyle, marginTop: 6 }}
                          value={contact.email || ''}
                          onChange={(event) => updateContact(index, 'email', event.target.value)}
                        />
                      </label>
                      <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                        Teléfono
                        <input
                          style={{ ...baseInputStyle, marginTop: 6 }}
                          value={contact.phone || ''}
                          onChange={(event) => updateContact(index, 'phone', event.target.value)}
                        />
                      </label>
                      <label style={{ fontSize: 12, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                        Estado
                        <select
                          style={{ ...baseInputStyle, marginTop: 6 }}
                          value={contact.status || 'Activo'}
                          onChange={(event) => updateContact(index, 'status', event.target.value)}
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => removeContact(index)}
                          style={{
                            height: 34,
                            padding: '0 12px',
                            borderRadius: 8,
                            border: `1px solid ${colors.inputBorder}`,
                            background: 'transparent',
                            color: '#b91c1c',
                            cursor: 'pointer',
                            fontSize: 12,
                          }}
                        >
                          Quitar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            );
          },
        },
      ],
    },
  ];

  return (
    <FormDrawer
      isOpen={isOpen}
      onClose={onClose}
      onSave={onSave}
      isSaving={isSaving}
      isDarkMode={isDarkMode}
      title={isEditing ? 'Editar proveedor' : 'Nuevo proveedor'}
      subtitle="Maestro de adquisiciones"
      sections={sections}
      formData={formData}
      onChange={onChange}
      formError={formError}
      width={720}
      zIndex={zIndex}
    />
  );
};

export default SuppliersForm;
